import { gql, QueryHookOptions, useQuery } from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLMerchantChannel,
  GQLQueryParty_GetPartyMerchantChannelsArgs,
  GQLMerchantChannelAndMerchantChannelCatalog,
  GQLQueryParty_GetPartyMerchantChannelAndMerchantChannelCatalogsArgs,
  GQLPartyConnection,
  GQLQueryParty_GetPartiesByPlatformArgs,
  GQLParty
} from '@jewelutions/core-crm-server-api-client-domain';
import { MERCHANT_CHANNEL_CATALOG_FIELDS, MERCHANT_CHANNEL_FIELDS } from './fragments/merchant';
import { PARTY_FIELDS } from './fragments/party';

export class PartyOperation {
  partyArrayFromResponse(data: any): GQLParty[] {
    if (data.Party_getPartiesByPlatform !== undefined) {
      return data.Party_getPartiesByPlatform.parties;
    }
    throw new Exception(`Cannot extract party array from expected fields in input data`);
  }

  merchantChannelAndMerchantChannelCatalogArrayFromResponse(
    data: any
  ): GQLMerchantChannelAndMerchantChannelCatalog[] {
    if (data === undefined) {
      throw new Exception(`Cannot extract merchant channel & catalog array from undefined input data`);
    }
    if (data.Party_getPartyMerchantChannelAndMerchantChannelCatalogs !== undefined) {
      return data.Party_getPartyMerchantChannelAndMerchantChannelCatalogs;
    }
    throw new Exception(`Cannot extract merchant channel & catalog array from expected fields in input data`);
  }

  merchantChannelArrayFromResponse(data: any): GQLMerchantChannel[] {
    if (data === undefined) {
      throw new Exception(`Cannot extract merchant channel array from undefined input data`);
    }
    if (data.Party_getPartyMerchantChannels !== undefined) {
      return data.Party_getPartyMerchantChannels;
    }
    throw new Exception(`Cannot extract merchant channel array from expected fields in input data`);
  }

  getPartiesByPlatform(
    args: GQLQueryParty_GetPartiesByPlatformArgs,
    options: QueryHookOptions<GQLPartyConnection, GQLQueryParty_GetPartiesByPlatformArgs> = {}
  ) {
    const query = gql`
      ${PARTY_FIELDS}
      query Party_getPartiesByPlatform($pageSize: Int = 10) {
        Party_getPartiesByPlatform(pageSize: $pageSize) {
          cursor
          hasMore
          parties {
            ...PartyFields
          }
        }
      }
    `;

    return useQuery<GQLPartyConnection, GQLQueryParty_GetPartiesByPlatformArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPartyMerchantChannels(
    args: GQLQueryParty_GetPartyMerchantChannelsArgs,
    options: QueryHookOptions<GQLMerchantChannel[], GQLQueryParty_GetPartyMerchantChannelsArgs> = {}
  ) {
    const query = gql`
      ${MERCHANT_CHANNEL_FIELDS}
      query Party_getPartyMerchantChannels($partyId: ID!) {
        Party_getPartyMerchantChannels(partyId: $partyId) {
          ...MerchantChannelFields
        }
      }
    `;

    return useQuery<GQLMerchantChannel[], GQLQueryParty_GetPartyMerchantChannelsArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPartyMerchantChannelAndMerchantChannelCatalogs(
    args: GQLQueryParty_GetPartyMerchantChannelsArgs,
    options: QueryHookOptions<
      GQLMerchantChannelAndMerchantChannelCatalog[],
      GQLQueryParty_GetPartyMerchantChannelAndMerchantChannelCatalogsArgs
    > = {}
  ) {
    const query = gql`
      ${MERCHANT_CHANNEL_FIELDS}
      ${MERCHANT_CHANNEL_CATALOG_FIELDS}
      query Party_getPartyMerchantChannelAndMerchantChannelCatalogs($partyId: ID!) {
        Party_getPartyMerchantChannelAndMerchantChannelCatalogs(partyId: $partyId) {
          merchantChannel {
            ...MerchantChannelFields
          }
          merchantChannelCatalog {
            ...MerchantChannelCatalogFields
          }
        }
      }
    `;

    return useQuery<
      GQLMerchantChannelAndMerchantChannelCatalog[],
      GQLQueryParty_GetPartyMerchantChannelAndMerchantChannelCatalogsArgs
    >(query, {
      ...options,
      variables: args
    });
  }
}
